import asserts from '@/shared/asserts';
import isNull from 'lodash/isNull';
import {
    AssetType,
    ChecklistStatus,
    HostSystem,
    NavigateDestination,
    PlayChecklistMode,
    PlayContentMode,
    PlayContentState,
    PlayProgress,
    PlaySection,
    PlayType,
} from '@/generated/play-api';
import { Dict, KeyValues, Struct } from '@/types/core-types';

export const BLANK_PLAY_ID = 'blank';

export const DEFAULT_SUGGESTION_COUNT = 6;

export enum MimeTypes {
    html = 'text/html',
    text = 'text/plain',
}

export type TransitionDefinition = {
    next: string;
    prev: string;
}

export enum TransitionType {
    next = 'next',
    prev = 'prev',
}

export const RECOMMENDATION_CATEGORY = 'recommended';

export enum ExportDestinationKey {
    unlayer = 'unlayer',
    unlayerJson = 'unlayerJson',
    html = 'html',
    text = 'text',
    simpleHtml = 'simpleHtml',
}

export enum TimingUnit {
    MINUTES = 'MINUTES',
    HOURS = 'HOURS',
    DAYS = 'DAYS',
    WEEKS = 'WEEKS',
    MONTHS = 'MONTHS',
}

export enum PlayEventTypes {
    PLAY_CATEGORY_CHANGED = 'Play Category Changed',
    PLAY_TEMPLATE_LIST_VIEWED = 'Play Template List Viewed',
    PLAY_TEMPLATE_PREVIEWED = 'Play Template Previewed',
    PLAY_QUESTIONS_STARTED = 'Play Questions Started',
    PLAY_SUGGESTION_REQUESTED = 'Play Suggestion Requested',
    PLAY_SUGGESTION_ACCEPTED = 'Play Suggestion Accept',
    PLAY_SUGGESTION_REJECTED = 'Play Suggestion Reject',
    PLAY_QUESTIONS_FINISHED = 'Play Questions Finished',
    PLAY_QUESTIONS_UPDATED = 'Play Questions Updated',
    PLAY_CONTENT_EDITED = 'Play Content Edited',
    PLAY_TEMPLATE_EDITED = 'Play Template Edited',
    PLAY_CONTENT_COPIED = 'Play Content Copied',
    PLAY_AUTOMATION_LAUNCHED = 'Play Automation Launched', // there,but commented
    PLAYS_VIEWED = 'My Plays Viewed',
    PLAY_OPENED = 'Completed Play Opened',
    PLAY_DELETED = 'Play Deleted',
    COACH_PLAY_TEMPLATE_TESTED = 'Coach Play Template Tested',
    COACH_PLAY_ADDED = 'Coach Added Play to Playbook',
}

export const DEFAULT_THEME_ID = 'kopy-default';

export enum SectionTypes {
    headline = 'headline',
    subheader = 'subheader',
    text = 'text',
    textBody = 'textBody',
    richTextBody = 'richTextBody',
}

/**
 * Normalizes sectionType values from API to expected format.
 * Handles conversion from SCREAMING_SNAKE_CASE (new API) to camelCase (expected format).
 */
export function normalizeSectionType(sectionType: string): SectionTypes {
    // Map SCREAMING_SNAKE_CASE enum values to camelCase
    const enumValueMap: Record<string, SectionTypes> = {
        RICH_TEXT_FIELD: SectionTypes.richTextBody,
        TEXT_BODY: SectionTypes.textBody,
        HEADLINE: SectionTypes.headline,
        SUBHEADER: SectionTypes.subheader,
        TEXT: SectionTypes.text,
    };

    // If it's already in the expected format, return it as-is
    if (Object.values(SectionTypes).includes(sectionType as SectionTypes)) {
        return sectionType as SectionTypes;
    }

    // Try to map from enum value
    if (enumValueMap[sectionType]) {
        return enumValueMap[sectionType];
    }

    // Fallback: try to convert SCREAMING_SNAKE_CASE to camelCase
    // RICH_TEXT_FIELD -> richTextBody
    const camelCase = sectionType
        .toLowerCase()
        .split('_')
        .map((word, index) => (index === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1)))
        .join('');

    // Check if the converted value matches a SectionType
    if (Object.values(SectionTypes).includes(camelCase as SectionTypes)) {
        return camelCase as SectionTypes;
    }

    // If no match found, return the original value (will likely cause an error downstream)
    return sectionType as SectionTypes;
}

/**
 * Normalizes enum values from API to uppercase format.
 * Handles conversion from lowercase/mixed case (incorrect API response) to uppercase (expected format).
 * This is backwards compatible - it accepts both uppercase (correct) and lowercase/mixed case (incorrect) values.
 *
 * @param value - The enum value to normalize
 * @param enumObject - The enum object to validate against
 * @param debugName - Optional name for debugging
 * @returns The normalized uppercase enum value, or the original value if it doesn't match any enum value
 */
function normalizeEnumValue<T extends string>(
    value: string | undefined | null,
    enumObject: Record<string, T>,
    debugName?: string,
): T | undefined | null {
    if (value == null) {
        return value as T | undefined | null;
    }

    // If it's already a valid enum value (case-sensitive match), return it as-is
    const enumValues = Object.values(enumObject) as string[];

    if (enumValues.includes(value)) {
        return value as T;
    }

    // Try case-insensitive match
    const upperValue = value.toUpperCase();
    const matchedValue = enumValues.find((ev) => ev.toUpperCase() === upperValue);

    if (matchedValue) {
        return matchedValue as T;
    }

    // Fall back to ignoring separators as well. The API emits SCREAMING_SNAKE while several
    // enums here are kebab-case, so 'KEAP_WEB' must still resolve to 'keap-web'.
    const squashed = upperValue.replace(/[-_]/g, '');
    const separatorMatch = enumValues.find((ev) => ev.toUpperCase().replace(/[-_]/g, '') === squashed);

    if (separatorMatch) {
        return separatorMatch as T;
    }

    // If no match found, return the original value (will likely cause an error downstream)
    return value as T;
}

/**
 * Normalizes AssetType enum values from SCREAMING_SNAKE_CASE (API format) to camelCase (enum value format).
 * Example: "SNIPPET_PAGE" -> "snippetPage", "HTML_PAGE" -> "htmlPage"
 */
export function normalizeAssetType(value: string | undefined | null, debugName?: string): string | undefined | null {
    if (value == null) {
        return value;
    }

    // If it's already a valid enum value (camelCase), return it as-is
    const enumValues = Object.values(AssetType) as string[];

    if (enumValues.includes(value)) {
        return value;
    }

    // Try to convert SCREAMING_SNAKE_CASE to camelCase
    // SNIPPET_PAGE -> snippetPage
    // HTML_PAGE -> htmlPage
    // EMAIL -> email (already lowercase)
    const camelCase = value
        .toLowerCase()
        .split('_')
        .map((word, index) => (index === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1)))
        .join('');

    // Check if the converted value matches an enum value
    if (enumValues.includes(camelCase)) {
        return camelCase;
    }

    // If no match found, return the original value
    return value;
}

/**
 * The destinations a partner chooses between in the Coaching Portal. A play lives in exactly
 * one of them. See playDestinations.
 */
export enum PlayDestination {
    keap = 'keap',
    marketingCenter = 'marketingCenter',
}

/**
 * Resolves an API host system to its enum member.
 *
 * Separator- and case-insensitive, mirroring play-service's HostSystem.of, because the
 * deployed API sends 'marketing-center' while its own spec lists the Java constant name.
 */
export function matchHostSystem(hostSystem?: string | null): HostSystem | undefined {
    const squash = (value?: string | null) => value?.toUpperCase().replace(/[-_]/g, '');
    const target = squash(hostSystem);

    return target ? Object.values(HostSystem).find((host) => squash(host) === target) : undefined;
}

/**
 * The destination a play surfaces in, as the Coaching Portal presents it: one home per play.
 *
 * Only MARKETING_CENTER reads as Marketing Center. Everything else — KEAP_WEB, CLASSIC, and
 * the legacy GENERAL / absent host systems that predate the destination picker — reads as
 * Keap, so no row is ever badge-less and none claims to live in two places.
 *
 * Returns a (single-element) list because badge and filter callers iterate destinations.
 */
export function playDestinations(hostSystem?: string | null): PlayDestination[] {
    switch (matchHostSystem(hostSystem)) {
    case HostSystem.MARKETING_CENTER:
        return [PlayDestination.marketingCenter];
    default:
        return [PlayDestination.keap];
    }
}

/**
 * The host system to persist for a chosen destination — the inverse of playDestinations.
 *
 * No choice stamps KEAP_WEB rather than leaving the field empty: play-service resolves a null
 * host system like GENERAL and would surface the play inside Marketing Center too, and the
 * Coaching Portal no longer means that by an unspecified destination.
 */
export function hostSystemForDestination(destination: PlayDestination | null): HostSystem {
    return destination === PlayDestination.marketingCenter
        ? HostSystem.MARKETING_CENTER
        : HostSystem.KEAP_WEB;
}

/**
 * Normalizes play enum fields from API response to expected format.
 * This ensures backwards compatibility when the API returns different enum value formats.
 *
 * @param play - The play object from the API
 * @param debug - Whether to log normalization changes (default: false)
 * @returns The play object with normalized enum fields
 */
export function normalizePlayEnums<T extends {
    playType?: string | null;
    playProgress?: string | null;
    checklistStatus?: string | null;
    state?: string | null;
    contentState?: string | null;
    contentMode?: string | null;
    checklistMode?: string | null;
    hostSystem?: string | null;
    assets?: Array<{ assetType?: string | null; [key: string]: unknown }> | null;
    [key: string]: unknown;
}>(play: T, debug = false): T {
    if (!play) {
        return play;
    }

    const normalized = { ...play };

    // Normalize each enum field
    if (normalized.playType != null) {
        normalized.playType = normalizeEnumValue(normalized.playType, PlayType, debug ? 'playType' : undefined) ?? normalized.playType;
    }

    if (normalized.playProgress != null) {
        normalized.playProgress = normalizeEnumValue(normalized.playProgress, PlayProgress, debug ? 'playProgress' : undefined) ?? normalized.playProgress;
    }

    if (normalized.checklistStatus != null) {
        normalized.checklistStatus = normalizeEnumValue(normalized.checklistStatus, ChecklistStatus, debug ? 'checklistStatus' : undefined) ?? normalized.checklistStatus;
    }

    if (normalized.state != null) {
        normalized.state = normalizeEnumValue(normalized.state, PlayContentState, debug ? 'state' : undefined) ?? normalized.state;
    }

    if (normalized.contentState != null) {
        normalized.contentState = normalizeEnumValue(normalized.contentState, PlayContentState, debug ? 'contentState' : undefined) ?? normalized.contentState;
    }

    if (normalized.contentMode != null) {
        normalized.contentMode = normalizeEnumValue(normalized.contentMode, PlayContentMode, debug ? 'contentMode' : undefined) ?? normalized.contentMode;
    }

    if (normalized.checklistMode != null) {
        normalized.checklistMode = normalizeEnumValue(normalized.checklistMode, PlayChecklistMode, debug ? 'checklistMode' : undefined) ?? normalized.checklistMode;
    }

    if (normalized.hostSystem != null) {
        normalized.hostSystem = normalizeEnumValue(normalized.hostSystem, HostSystem, debug ? 'hostSystem' : undefined) ?? normalized.hostSystem;
    }

    // Normalize assetType in assets array
    if (normalized.assets && Array.isArray(normalized.assets)) {
        normalized.assets = normalized.assets.map((asset, index) => {
            if (asset?.assetType != null) {
                const normalizedAssetType = normalizeAssetType(asset.assetType, debug ? `assets[${index}].assetType` : undefined);

                return {
                    ...asset,
                    assetType: normalizedAssetType ?? asset.assetType,
                };
            }

            return asset;
        });
    }

    return normalized;
}

export const dateExpressionNow = 'now';

export enum LaunchLinkType {
    landingPage = 'landingPage',
    sequence = 'automation',
    trigger = 'trigger'
}

export const LaunchLinkToNavDestination = <Dict<string>>{
    [LaunchLinkType.landingPage]: NavigateDestination.LANDING_PAGE,
    [LaunchLinkType.sequence]: NavigateDestination.EASY_AUTOMATION,
};

export const LaunchLinkTypeName = <Dict<string>>{
    sequence: 'Easy Automation',
    trigger: 'Trigger',
    landingPage: 'Landing Page',
};

export enum PlayStartType {
    appointment = 'appointment',
    landingPage = 'landingPage',
    form = 'form',
    stageMove = 'stageMove',
    tag = 'tag',
    productPurchased = 'productPurchased',
}

export const PlayStepType = {
    asset: 'asset',
    timer: 'timer',
};

export const PlayAssetType = {
    email: 'email',
    landingPage: 'landingPage',
    htmlPage: 'htmlPage',
    snippetPage: 'snippetPage',
    socialSnippets: 'socialSnippets',
    emailPs: 'emailPs',
    sms: 'sms',
    instructions: 'instructions',
    content: 'content',
};

export const PLAY_STAGE_STATUS = {
    SETUP: 'setup',
    CONTENT: 'content',
    AUTOMATION_CREATED: 'automation_created',
};

/**
 * Describes how we should treat each content type:
 *
 * - html: Does it support html?
 * - multiLine: does it support multiple lines?
 */
export const sectionTypeMeta = {
    [SectionTypes.headline]: {
        destinations: [ExportDestinationKey.text],
        multiLine: false,
    },

    [SectionTypes.text]: {
        destinations: [ExportDestinationKey.text],
        multiLine: false,
    },

    [SectionTypes.subheader]: {
        destinations: [ExportDestinationKey.text],
        multiLine: false,
    },

    [SectionTypes.textBody]: {
        destinations: [ExportDestinationKey.text],
        multiLine: true,
    },

    [SectionTypes.richTextBody]: {
        destinations: [
            ExportDestinationKey.text,
            ExportDestinationKey.html,
            ExportDestinationKey.unlayer,
        ],
        multiLine: true,
    },
};

function bodySectionReducer(sections: PlaySection[]): PlaySection[] {
    if (sections?.length < 1) {
        return sections;
    }
    const [firstSection] = sections;
    const combinedContent = sections.map((sec) => sec.content).filter(notNull).join('\n');
    const targetSection = sections.find(({ sectionKey }) => sectionKey === 'body') ?? firstSection;

    targetSection.content = combinedContent;
    targetSection.sectionKey = 'body';

    return [targetSection];
}

export type BodySectionReducer = ((sections: PlaySection[]) => PlaySection[]);
export type AssetTypeMeta = {
    preferredExport: ExportDestinationKey;
    sectionReducer: BodySectionReducer;
    destinations: ExportDestinationKey[];
}

const echoSectionReducer: BodySectionReducer = ((sections: PlaySection[]) => sections);

function _AssetTypeMeta(source: {
    preferredExport: ExportDestinationKey,
    sectionReducer?: BodySectionReducer,
    destinations?: ExportDestinationKey[],
}): AssetTypeMeta {
    const { destinations = [], preferredExport, sectionReducer = echoSectionReducer } = source;

    asserts(preferredExport, 'Must provide default export');

    return <AssetTypeMeta>{
        destinations,
        preferredExport,
        sectionReducer,
    };
}

export const assetTypeMeta = <Struct<AssetType, AssetTypeMeta>>{
    [AssetType.LANDING_PAGE]: _AssetTypeMeta({
        preferredExport: ExportDestinationKey.simpleHtml,
        destinations: [ExportDestinationKey.text, ExportDestinationKey.simpleHtml, ExportDestinationKey.unlayer],
        sectionReducer: bodySectionReducer,
    }),

    [AssetType.HTML_PAGE]: _AssetTypeMeta({
        preferredExport: ExportDestinationKey.simpleHtml,
        destinations: [ExportDestinationKey.simpleHtml, ExportDestinationKey.text, ExportDestinationKey.unlayer],
        sectionReducer: bodySectionReducer,
    }),

    [AssetType.SMS]: _AssetTypeMeta({
        preferredExport: ExportDestinationKey.text,
    }),

    [AssetType.SNIPPET_PAGE]: _AssetTypeMeta({
        preferredExport: ExportDestinationKey.simpleHtml,
        destinations: [ExportDestinationKey.simpleHtml],
    }),

    [AssetType.SOCIAL_SNIPPETS]: _AssetTypeMeta({
        preferredExport: ExportDestinationKey.text,
        destinations: [ExportDestinationKey.simpleHtml],
    }),

    [AssetType.EMAIL]: _AssetTypeMeta({
        preferredExport: ExportDestinationKey.simpleHtml,
    }),

    [AssetType.EMAIL_PS]: _AssetTypeMeta({
        preferredExport: ExportDestinationKey.text,
    }),

    [AssetType.CONTENT]: _AssetTypeMeta({
        preferredExport: ExportDestinationKey.simpleHtml,
    }),

    [AssetType.INSTRUCTIONS]: _AssetTypeMeta({
        preferredExport: ExportDestinationKey.simpleHtml,
    }),
};

export const metaKeys = {
    suggestions: 'suggestions',
};

asserts.containsKeys(assetTypeMeta, Object.values(AssetType), 'assetTypeMeta');

export enum PlayLifecycleEvent {
    close = 'close',
    doneWithQuestions = 'done-with-questions',
    saveAnswers = 'save-answers',
    beforeChangePage = 'before-change-page',
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const notNull = (x: any) => !isNull(x);
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const notFalsy = (x: any) => Boolean(x);

export function byName(nameLiteral: string) {
    return (x?: KeyValues | null) => x?.name === nameLiteral;
}

export const alwaysTrue = () => true;

export enum PlayQuestionMode {
    inline = 'INLINE',
    normal = 'NORMAL',
}

export enum ArrayEvent {
    remove = 'remove',
    move = 'move', // { item: element, oldIndex, newIndex }
    update = 'update',
    add = 'add',
    changeItem = 'change-item',
}

export type ArrayEventPayload = KeyValues & {
    index: number;
    oldIndex: number;
}

export const SEARCHING_DEBOUNCE_DELAY = 1000;

export const VALUE_ALT_REPLACE_PATTERN = '__VALUE__';
export const VALUE_LIQUID_REPLACE_PATTERN = '{{ value }}';
export const MAX_AI_ATTEMPTS = 5;

export enum ProviderName {
    suggestionsProvider = 'suggestionsProvider',
    playProvider = 'playProvider',
    hostProvider = 'hostProvider',
    accountSwitcherProvider = 'accountSwitcherProvider',
    questionProvider = 'questionProvider',
    playServiceProvider = 'playServiceProvider',
    modelProvider = 'modelProvider',
    modelEditProvider = 'modelEditProvider',
    propInfoProvider = 'propInfoProvider',
    coreProvider = 'coreProvider',
    feedbackProvider = 'feedbackProvider',
}

export enum PlayStates {
    questions = 'QUESTIONS',
    content = 'CONTENT',
}

export const playCategories = [
    {
        category: 'By LCM Phase',
        columns:
            [
                {
                    column: 'Collect Leads',
                    columnDescription: 'Capture information to turn visitors, fans and contacts into leads',
                    columnIllustration: 'leads-color',
                    topPlays: [
                        'Lead Magnet Download',
                        'Lead Magnet Offer Broadcast',
                        'Schedule a Free Consult',
                        'Free Consult Invites',
                    ],
                    otherPlays: [
                        'Checklist Generator',
                        'Free Guide Builder',
                        'All Questions',
                        'Eric Test Template',

                    ],
                },
                {
                    column: 'Convert Clients',
                    columnDescription: 'Move leads through your sales proces and convert them to clients',
                    columnIllustration: 'successful-payment-color',
                    topPlays: [
                        'Lead Magnet Follow Up (To Consult)',
                        'Lead Magnet Follow Up (Sales Offer)',
                        'Post-Consult Follow-Up + Offer',

                    ],
                    otherPlays: [
                        '',
                    ],
                },
                {
                    column: 'Create Fans',
                    columnDescription: 'Wow new customers and turn them into fans who buy more & refer',
                    columnIllustration: 'success-high-five-color',
                    topPlays: [
                        'New Customer Welcome (Services)',
                        'Testimonial Request',
                        'Sales Offer (Made-Up Holiday)',
                        'Sales Offer (Generic)',
                        'Sales Offer (Generic v2)',
                    ],
                    otherPlays: [
                        '',
                    ],
                },
            ],
    },
    {
        category: 'By Play Type',
        columns:
            [
                {
                    column: 'Lead Magnets',
                    columnDescription: 'Plays that leverage various Lead Magnets',
                    topPlays: [
                        'Lead Magnet Download',
                        'Lead Magnet Offer Broadcast',
                        'Lead Magnet Follow Up (To Consult)',
                        'Lead Magnet Follow Up (Sales Offer)',
                        'Checklist Generator',
                        'Free Guide Builder',
                        '',
                    ],
                    otherPlays: [
                        '',
                    ],
                },
                {
                    column: 'Free Consultation',
                    columnDescription: 'Plays that get the consult scheduled and follow through to a sale',
                    topPlays: [
                        'Schedule a Free Consult',
                        'Free Consult Invites',
                        'Consult Reminders + Pre-suasion',
                        'Post-Consult Follow-Up + Offer',

                    ],
                    otherPlays: [
                        '',
                    ],
                },
                {
                    column: 'Sales Offers',
                    columnDescription: 'Plays that wow your new customers and turn them into fans',
                    topPlays: [
                        'Sales Offer (Made-Up Holiday)',
                        'Sales Offer (Generic)',
                        'Sales Offer (Generic v2)',
                    ],
                    otherPlays: [
                        '',
                    ],
                },
            ],
    },
];

export const CAPABILITY_PLAY_LAUNCH = 'play.launch';
export const CAPABILITY_USER_LOG_EVENT = 'user.logEvent';

export const capabilityKeys = {
    launchPlay: CAPABILITY_PLAY_LAUNCH,
    userLogEvent: CAPABILITY_USER_LOG_EVENT,
};
